import { Component, OnDestroy, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { TenantSignupService } from '../../services/tenant-signup.service';
import { ApiResponse } from '../../models';
import { SubdomainCheckDto, TenantSignupRequestDto } from '../../models/tenant-signup';
import { TenantModule, TenantType } from '../../services/tenant-features.service';
import { defaultsForOrgType } from '../../services/tenant-modules';

/**
 * Super-admin (Administrator only) review of public "join as a tenant" signup requests.
 * Lists incoming leads, filter by status, and approve / reject them. Approving a request
 * for the first time provisions the tenant + a TenantAdmin user and emails/WhatsApps the
 * admin a set-password link (the admin confirms the subdomain in the approve dialog, with
 * a live availability check). The link can be re-sent for already-provisioned requests.
 */
@Component({
  selector: 'app-tenant-signups',
  templateUrl: './tenant-signups.component.html',
  styleUrl: './tenant-signups.component.scss',
})
export class TenantSignupsComponent implements OnInit, OnDestroy {
  requests: TenantSignupRequestDto[] = [];
  isLoading = true;
  totalItems = 0;
  pageNumber = 1;
  pageSize = 10;
  statusFilter = '';
  expandedId: string | null = null;

  readonly statuses = ['', 'Pending', 'Approved', 'Rejected'];

  // ── Approve dialog state ──────────────────────────────────────────────────
  approveTarget: TenantSignupRequestDto | null = null;
  subdomainInput = '';
  checking = false;
  checkResult: SubdomainCheckDto | null = null;
  submitting = false;
  /** Organisation type + modules for the new tenant, pre-filled from the request's organisation type. */
  approveType: TenantType = TenantType.Mixed;
  approveModules: TenantModule[] = [];

  private readonly subdomain$ = new Subject<string>();
  private subdomainSub?: Subscription;

  constructor(
    private signupSrv: TenantSignupService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.load(1);

    this.subdomainSub = this.subdomain$
      .pipe(
        debounceTime(350),
        distinctUntilChanged(),
        switchMap(value => {
          this.checking = true;
          return this.signupSrv.checkSubdomain(value);
        })
      )
      .subscribe({
        next: res => {
          this.checking = false;
          this.checkResult = res.isSuccess ? res.value : null;
        },
        error: () => {
          this.checking = false;
          this.checkResult = null;
        },
      });
  }

  ngOnDestroy(): void {
    this.subdomainSub?.unsubscribe();
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalItems / this.pageSize));
  }

  load(page: number): void {
    this.isLoading = true;
    this.pageNumber = page;
    this.signupSrv.list(page, this.pageSize, this.statusFilter || undefined).subscribe({
      next: (res: ApiResponse<TenantSignupRequestDto[]>) => {
        if (res.isSuccess && res.value) {
          this.requests = res.value;
          this.totalItems = res.totalCount;
        }
        this.isLoading = false;
      },
      error: () => {
        this.toastr.error('Failed to load signup requests.');
        this.isLoading = false;
      },
    });
  }

  onFilterChange(): void {
    this.load(1);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.pageNumber) return;
    this.load(page);
  }

  toggleDetails(id: string): void {
    this.expandedId = this.expandedId === id ? null : id;
  }

  // ── Approve flow (dialog + live availability) ─────────────────────────────
  openApproveDialog(req: TenantSignupRequestDto): void {
    // Already provisioned → approving again is a no-op; just flip the status.
    if (req.isProvisioned) {
      this.sendStatus(req, 'Approved');
      return;
    }
    this.approveTarget = req;
    const defaults = defaultsForOrgType(req.organizationType);
    this.approveType = defaults.type;
    this.approveModules = defaults.modules;
    this.subdomainInput = req.preferredSubdomain || this.slugify(req.organizationName);
    this.checkResult = null;
    this.checking = false;
    if (this.subdomainInput) this.subdomain$.next(this.subdomainInput);
  }

  closeApproveDialog(): void {
    this.approveTarget = null;
    this.subdomainInput = '';
    this.checkResult = null;
    this.checking = false;
  }

  onSubdomainInput(): void {
    const value = this.slugify(this.subdomainInput);
    if (value !== this.subdomainInput) this.subdomainInput = value;
    this.checkResult = null;
    if (value) this.subdomain$.next(value);
  }

  get canConfirmApprove(): boolean {
    return !this.submitting && !this.checking && !!this.checkResult?.available && this.approveModules.length > 0;
  }

  confirmApprove(): void {
    if (!this.approveTarget || !this.canConfirmApprove) return;
    const req = this.approveTarget;
    const subdomain = this.subdomainInput;

    this.submitting = true;
    this.signupSrv.updateStatus(req.id, 'Approved', subdomain, this.approveType, this.approveModules).subscribe({
      next: res => {
        this.submitting = false;
        if (res.isSuccess) {
          req.status = 'Approved';
          req.isProvisioned = true;
          this.toastr.success(`Tenant provisioned — a set-password link was sent to ${req.email}.`);
          this.closeApproveDialog();
        } else {
          this.toastr.error(res.errors?.[0] ?? 'Unexpected response from the server.');
        }
      },
      error: err => {
        this.submitting = false;
        this.toastr.error(err?.error?.errors?.[0]?.messageEn ?? err?.error?.errors?.[0] ?? 'Failed to approve the request.');
      },
    });
  }

  reject(req: TenantSignupRequestDto): void {
    this.sendStatus(req, 'Rejected');
  }

  resend(req: TenantSignupRequestDto): void {
    this.isLoading = true;
    this.signupSrv.resend(req.id).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.isSuccess) this.toastr.success(`Set-password link re-sent to ${req.email}.`);
        else this.toastr.error(res.errors?.[0] ?? 'Could not re-send the link.');
      },
      error: err => {
        this.isLoading = false;
        this.toastr.error(err?.error?.errors?.[0]?.messageEn ?? 'Could not re-send the link.');
      },
    });
  }

  deleteRequest(req: TenantSignupRequestDto): void {
    const msg = req.isProvisioned
      ? `Delete this signup record for ${req.email}? The provisioned tenant will NOT be affected.`
      : `Delete this signup request from ${req.email}?`;
    if (!window.confirm(msg)) return;

    this.isLoading = true;
    this.signupSrv.delete(req.id).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.isSuccess) {
          this.requests = this.requests.filter(r => r.id !== req.id);
          this.totalItems = Math.max(0, this.totalItems - 1);
          this.toastr.success('Signup request deleted.');
        } else {
          this.toastr.error(res.errors?.[0] ?? 'Could not delete the request.');
        }
      },
      error: err => {
        this.isLoading = false;
        this.toastr.error(err?.error?.errors?.[0]?.messageEn ?? 'Could not delete the request.');
      },
    });
  }

  /** Simple status flip for Reject / re-Approve (no provisioning). */
  private sendStatus(req: TenantSignupRequestDto, status: 'Approved' | 'Rejected' | 'Pending'): void {
    if (req.status === status) return;
    this.isLoading = true;
    this.signupSrv.updateStatus(req.id, status).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.isSuccess) {
          req.status = status;
          this.toastr.success(`Request marked ${status}.`);
        } else {
          this.toastr.error(res.errors?.[0] ?? 'Unexpected response from the server.');
        }
      },
      error: err => {
        this.isLoading = false;
        this.toastr.error(err?.error?.errors?.[0]?.messageEn ?? 'Failed to update the request.');
      },
    });
  }

  private slugify(input: string): string {
    return input.trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/-{2,}/g, '-').replace(/^-|-$/g, '');
  }

  badgeClass(status: string): string {
    switch (status) {
      case 'Approved': return 'badge bg-success';
      case 'Rejected': return 'badge bg-danger';
      default: return 'badge bg-warning text-dark';
    }
  }
}
